'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  BodyLarge,
  MetaDefault,
  MonoDefault,
  MonoSmall,
  TimelineConnector,
} from '@terra/ui-design-system';
import type { DateFormatter } from '@terra/ui-formatters';
import {
  CheckmarkCircle01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  PieChartIcon,
  CancelCircleIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { capitalize } from 'radash';
import type {
  ExpectedResponseAttempt,
  ExpectedResponseMatchResult,
  ExpectedResponseObservationTableRow,
} from '../../utils/group-expected-response-observations';

type AuditLogFlowProps = {
  rows: ExpectedResponseObservationTableRow[];
  dateFormatter: DateFormatter;
  onNavigateToToolCall?: (toolCallId: string) => void;
};
const MATCH_ICON_BY_RESULT: Record<
  ExpectedResponseMatchResult,
  typeof CheckmarkCircle01Icon
> = {
  full: CheckmarkCircle01Icon,
  status_only: PieChartIcon,
  unmatched: CancelCircleIcon,
};
// Timeline dots are monochrome — the icon shape carries the distinction; the
// color lives on the match badge instead.
const MATCH_ICON_COLOR = 'var(--colors-fg-default)';
// Match badge coloring mirrors Badge's own BASIC_BADGE_STYLE convention. The
// two forms are intentional, not inconsistent — they follow how Badge applies
// each: `color` is forwarded to Badge's inner <Text color={...}>, so it stays a
// token path (Panda resolves it to the pre-generated color:var(--colors-fg-*)
// class); `background` is applied by Badge via an inline style, so it must be a
// resolved var string — token() gives us that (bg.success → var(--colors-bg-success)).
const MATCH_BADGE_STYLE: Record<
  ExpectedResponseMatchResult,
  {
    color: string;
    background: string;
  }
> = {
  full: {
    color: 'fg.success',
    background: token('colors.bg.success'),
  },
  status_only: {
    color: 'fg.warning',
    background: token('colors.bg.warning'),
  },
  unmatched: {
    color: 'fg.error',
    background: token('colors.bg.error'),
  },
};
// Maps the stored verb enum (NONE/CREATE/UPDATE/DELETE) to a title-case,
// user-facing action label. NONE is a read (non-state-changing request).
const ACTION_LABEL_KEY_BY_VERB: Record<string, string> = {
  NONE: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

function useActionLabel(verb: string | null): string | null {
  const t = useTranslations('expectedResponseObservations');

  if (!verb) {
    return null;
  }

  const key = ACTION_LABEL_KEY_BY_VERB[verb];

  return key ? t(`actionLabel.${key}`) : capitalize(verb);
}

function MatchBadge({
  matchResult,
}: {
  matchResult: ExpectedResponseMatchResult;
}) {
  const t = useTranslations('expectedResponseObservations');
  const label = t(
    `match.${matchResult === 'status_only' ? 'statusOnly' : matchResult}`,
  );
  const badgeStyle = MATCH_BADGE_STYLE[matchResult];

  return (
    <Badge
      size="sm"
      variant="fill"
      fontWeight="medium"
      bg={badgeStyle.background}
      color={badgeStyle.color}
    >
      {label}
    </Badge>
  );
}

function FlowDot({
  matchResult,
}: {
  matchResult: ExpectedResponseMatchResult;
}) {
  const MatchIcon = MATCH_ICON_BY_RESULT[matchResult];

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={9}
      height={9}
      borderRadius="full"
      bg="bg.default"
      borderWidth="1px"
      borderColor="border.subtle"
      flexShrink={0}
    >
      <MatchIcon size={16} color={MATCH_ICON_COLOR} />
    </Box>
  );
}

function StatusPill({ status }: { status: number }) {
  return (
    <Box bg="bg.muted" borderRadius="full" px={2} py={0.5} flexShrink={0}>
      <MonoSmall color="fg.default" fontVariantNumeric="tabular-nums">
        {status}
      </MonoSmall>
    </Box>
  );
}

type RequestsListProps = {
  attempts: ExpectedResponseAttempt[];
  dateFormatter: DateFormatter;
  onNavigateToToolCall?: (toolCallId: string) => void;
};

function RequestsList({
  attempts,
  dateFormatter,
  onNavigateToToolCall,
}: RequestsListProps) {
  const t = useTranslations('expectedResponseObservations');
  const isInteractive = Boolean(onNavigateToToolCall);

  return (
    <VStack gap={0} alignItems="stretch" w="full" mt={1} px={2}>
      {attempts.map((attempt, index) => {
        const handleClick = () => {
          if (onNavigateToToolCall) {
            onNavigateToToolCall(attempt.toolCallId);
          }
        };

        return (
          <HStack
            key={attempt.id}
            justifyContent="space-between"
            alignItems="center"
            gap={3}
            py={2.5}
            borderTopWidth={index === 0 ? '0' : '1px'}
            borderColor="border.subtle"
            cursor={isInteractive ? 'pointer' : 'default'}
            onClick={isInteractive ? handleClick : undefined}
            title={isInteractive ? t('requests.goToStep') : undefined}
            _hover={isInteractive ? { opacity: 0.85 } : undefined}
          >
            <BodyDefault color="fg.muted" fontVariantNumeric="tabular-nums">
              {dateFormatter.formatTimeOnly(new Date(attempt.recordedAt))}
            </BodyDefault>
            <HStack gap={2} alignItems="center" flexShrink={0}>
              <StatusPill status={attempt.observedStatus} />
              <MatchBadge matchResult={attempt.matchResult} />
              {isInteractive ? (
                <ArrowRight01Icon size={14} color="var(--colors-fg-muted)" />
              ) : null}
            </HStack>
          </HStack>
        );
      })}
    </VStack>
  );
}

type AuditLogFlowItemProps = {
  row: ExpectedResponseObservationTableRow;
  isLast: boolean;
  dateFormatter: DateFormatter;
  onNavigateToToolCall?: (toolCallId: string) => void;
};

function AuditLogFlowItem({
  row,
  isLast,
  dateFormatter,
  onNavigateToToolCall,
}: AuditLogFlowItemProps) {
  const t = useTranslations('expectedResponseObservations');
  const [expanded, setExpanded] = useState(false);
  const actionLabel = useActionLabel(row.expectedAction);
  // Matched/partial rows carry a code-derived description; unmatched ones don't
  // (the observed response matched no expected-response rule for the resolved
  // endpoint), so they fall back to a generic title.
  const title = row.description ?? t('noMatchTitle');

  return (
    <HStack alignItems="stretch" w="full" gap={4}>
      <VStack alignItems="center" gap={0} flexShrink={0}>
        <FlowDot matchResult={row.matchResult} />
        {!isLast ? <TimelineConnector /> : null}
      </VStack>

      <Box flex={1} minWidth={0} pb={isLast ? 0 : 6}>
        {/* Title + inline match badge */}
        <HStack gap={2} alignItems="center" flexWrap="wrap">
          <BodyLarge color="fg.default" lineHeight="snug">
            {title}
          </BodyLarge>
          <MatchBadge matchResult={row.matchResult} />
        </HStack>

        {/* Request card */}
        <Box
          mt={2}
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="lg"
          bg="bg.subtle"
          px={4}
          py={3}
        >
          <HStack
            justifyContent="space-between"
            alignItems="flex-start"
            gap={3}
          >
            <MonoDefault
              color="fg.default"
              minWidth={0}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {row.endpointLabel}
            </MonoDefault>
            {actionLabel ? (
              <BodyDefault as="span" color="fg.muted" flexShrink={0}>
                {actionLabel}
              </BodyDefault>
            ) : null}
          </HStack>
          {row.expectedEntity ? (
            <MonoSmall color="fg.muted" display="block" mt={1}>
              {row.expectedEntity}
            </MonoSmall>
          ) : null}
        </Box>

        {/* Show / hide the individual requests */}
        <Box
          as="button"
          display="inline-flex"
          alignItems="center"
          gap={1}
          mt={2}
          bg="transparent"
          border="none"
          p={0}
          cursor="pointer"
          color="fg.muted"
          onClick={() => setExpanded((current) => !current)}
        >
          <BodyDefault as="span" color="fg.muted">
            {expanded ? t('requests.hide') : t('requests.show')}
          </BodyDefault>
          {expanded ? (
            <ArrowUp01Icon size={14} />
          ) : (
            <ArrowDown01Icon size={14} />
          )}
        </Box>

        {expanded ? (
          <RequestsList
            attempts={row.attempts}
            dateFormatter={dateFormatter}
            onNavigateToToolCall={onNavigateToToolCall}
          />
        ) : null}
      </Box>
    </HStack>
  );
}

// The Neo-run observations API returns up to 500 rows; cap the DOM to keep the
// trace tab responsive and prompt the user to narrow via filters instead.
const MAX_VISIBLE_ROWS = 100;

export function AuditLogFlow({
  rows,
  dateFormatter,
  onNavigateToToolCall,
}: AuditLogFlowProps): ReactNode {
  const t = useTranslations('expectedResponseObservations');

  if (rows.length === 0) {
    return null;
  }

  const visibleRows = rows.slice(0, MAX_VISIBLE_ROWS);

  return (
    <VStack gap={0} alignItems="stretch" w="full" pt={1}>
      {visibleRows.map((row, index) => (
        <AuditLogFlowItem
          key={row.id}
          row={row}
          isLast={index === visibleRows.length - 1}
          dateFormatter={dateFormatter}
          onNavigateToToolCall={onNavigateToToolCall}
        />
      ))}
      {rows.length > visibleRows.length ? (
        <Box pt={4} textAlign="center">
          <MetaDefault color="fg.muted">
            {t('flow.truncated', {
              shown: visibleRows.length,
              total: rows.length,
            })}
          </MetaDefault>
        </Box>
      ) : null}
    </VStack>
  );
}
